import { useEffect, useRef, useState } from "react";
import { STATUS_OPTIONS, type StatusId } from "#shared/constants.ts";
import type { TrackRow } from "#shared/schemas/trackQuery.ts";
import { notifyError } from "@web/features/diagnostics/notifyError.ts";
import { api } from "@web/lib/api.ts";
import { useQueryClient } from "@tanstack/react-query";
import type { GridColumnMeta } from "./columns.tsx";

function fail(err: unknown) {
  notifyError(err, { fallbackCode: "SAVE_FAILED", actionLabel: "save cell" });
}

/** Inline status edit only — custom fields are edited via the sheet / bulk bar. */
export function EditableCell({
  track,
  meta,
  display,
}: {
  track: TrackRow;
  meta: GridColumnMeta;
  display: React.ReactNode;
  onSaved?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const qc = useQueryClient();
  const inputRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  if (meta.editable !== "status") return <>{display}</>;

  if (!editing) {
    return (
      <button
        type="button"
        className="min-w-0 flex-1 truncate text-left"
        onDoubleClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
        onClick={(e) => e.stopPropagation()}
        title="Double-click to edit status"
      >
        {display}
      </button>
    );
  }

  const current = track.status?.status ?? "none";

  async function save(status: string) {
    try {
      await api(`/api/entities/track/${track.id}/status`, {
        method: "PATCH",
        json: { status: status as StatusId },
      });
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ["tracks-infinite"] });
    } catch (err) {
      fail(err);
      setEditing(false);
    }
  }

  return (
    <select
      ref={inputRef}
      className="coykoi-grid-cell-edit"
      defaultValue={current}
      onBlur={(e) => void save(e.currentTarget.value)}
      onChange={(e) => void save(e.currentTarget.value)}
      onKeyDown={(e) => {
        if (e.key === "Escape") setEditing(false);
      }}
    >
      {STATUS_OPTIONS.map((opt) => (
        <option key={opt.id} value={opt.id}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}
